import { useI18n, type MessageKey } from '@ecom/i18n';
import { Button, Card, EmptyState, Skeleton } from '@ecom/ui-kit';
import { type ReactElement, useState } from 'react';

import { useKpisQuery, useTrendsQuery } from '../api';
import { AdminsTable } from '../components/AdminsTable';
import { DashboardSection } from '../components/DashboardSection';
import { FreshnessBar } from '../components/FreshnessBar';
import { KpiTile, type TileTone } from '../components/KpiTile';
import {
  RecentReassignments,
  TopMerchantsTable,
  TopProductsTable,
} from '../components/RankingTables';
import { ToHandleTable } from '../components/ToHandleTable';
import { TrendChart } from '../components/TrendChart';
import { formatCount, formatTnd } from '../format';
import {
  TREND_RANGES,
  type DashboardKpis,
  type DashboardTrends,
  type TrendRangeId,
} from '../types';

const RANGE_LABEL: Readonly<Record<TrendRangeId, MessageKey>> = {
  '7d': 'admin.dashboard.trend.r7',
  '30d': 'admin.dashboard.trend.r30',
  '90d': 'admin.dashboard.trend.r90',
  '12m': 'admin.dashboard.trend.r12m',
};

/** A figure that signals trouble when it is not zero. */
const warnWhenPositive = (value: number): TileTone => (value > 0 ? 'warn' : 'neutral');

/**
 * The superadmin's home (lot K3): the whole platform at a glance. Every figure comes from `GET /api/v1/admin/dashboard/kpis` and the
 * curves from `/trends` (lot K2, superadmin only, audited as `dashboard.kpi`, cached 60 seconds). The screen holds no logic of its
 * own about WHO may see it - the route is a convenience, the server's refusal (403) is the barrier, and this page says so in words.
 *
 * Blocks, in the order of the brief: financial, merchants, to handle, curves, administrators, data quality, security, products, top
 * merchants. A card with a list behind it is a link to that list, already filtered.
 */
export function DashboardPage(): ReactElement {
  const { t } = useI18n();
  const kpis = useKpisQuery();
  const [range, setRange] = useState<TrendRangeId>('30d');
  const trends = useTrendsQuery(range);

  const refreshing = kpis.isFetching || trends.isFetching;
  const refresh = (): void => {
    void kpis.refetch();
    void trends.refetch();
  };

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-8" data-testid="dashboard-page">
      <header className="flex flex-col gap-2">
        <h1 className="font-display text-2xl font-semibold text-ink">
          {t('admin.dashboard.title')}
        </h1>
        <p className="text-sm text-inkSoft">{t('admin.dashboard.subtitle')}</p>
        <FreshnessBar
          generatedAt={kpis.data?.generatedAt ?? null}
          refreshing={refreshing}
          onRefresh={refresh}
        />
      </header>

      {kpis.isPending ? (
        <DashboardSkeleton />
      ) : kpis.isError ? (
        <DashboardError forbidden={kpis.error.status === 403} onRetry={refresh} />
      ) : kpis.data.merchants.total === 0 && kpis.data.financial.ordersTotal === 0 ? (
        <EmptyState
          title={t('admin.dashboard.empty.title')}
          description={t('admin.dashboard.empty.text')}
        />
      ) : (
        <DashboardBlocks data={kpis.data} range={range} onRange={setRange} trends={trends} />
      )}
    </div>
  );
}

function DashboardSkeleton(): ReactElement {
  return (
    <div className="flex flex-col gap-6" data-testid="dashboard-loading">
      <Skeleton shape="text" width="12rem" labelled />
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4">
        {Array.from({ length: 8 }, (_, index) => (
          <Card key={index} className="p-4">
            <Skeleton shape="text" width="60%" />
            <div className="mt-3">
              <Skeleton shape="block" height="1.75rem" width="75%" />
            </div>
          </Card>
        ))}
      </div>
      <Skeleton shape="block" height="12rem" />
    </div>
  );
}

function DashboardError({
  forbidden,
  onRetry,
}: {
  readonly forbidden: boolean;
  readonly onRetry: () => void;
}): ReactElement {
  const { t } = useI18n();
  return (
    <Card className="flex flex-col items-start gap-3 p-6" data-testid="dashboard-error">
      <p role="alert" className="font-display text-lg font-bold text-critical">
        {t('admin.dashboard.error.title')}
      </p>
      <p className="text-sm text-inkSoft">
        {forbidden ? t('admin.dashboard.error.forbidden') : t('admin.dashboard.error.text')}
      </p>
      {forbidden ? null : (
        <Button variant="secondary" onClick={onRetry}>
          {t('admin.dashboard.retry')}
        </Button>
      )}
    </Card>
  );
}

function DashboardBlocks({
  data,
  range,
  onRange,
  trends,
}: {
  readonly data: DashboardKpis;
  readonly range: TrendRangeId;
  readonly onRange: (range: TrendRangeId) => void;
  readonly trends: {
    readonly data: DashboardTrends | undefined;
    readonly isPending: boolean;
    readonly isError: boolean;
  };
}): ReactElement {
  const { t, locale } = useI18n();
  const { financial, merchants, admins, products, security } = data;
  const count = (value: number): string => formatCount(value, locale);
  const tnd = (value: number): string => formatTnd(value, locale);

  return (
    <>
      <DashboardSection
        title={t('admin.dashboard.section.financial')}
        tiles
        money
        testId="block-financial"
      >
        <KpiTile
          label={t('admin.dashboard.fin.salesTotal')}
          value={tnd(financial.salesTotal)}
          hint={t('admin.dashboard.fin.excludesCancelled')}
          testId="kpi-sales-total"
        />
        <KpiTile
          label={t('admin.dashboard.fin.revenueToday')}
          value={tnd(financial.revenueToday)}
          testId="kpi-revenue-today"
        />
        <KpiTile
          label={t('admin.dashboard.fin.revenueMonth')}
          value={tnd(financial.revenueMonth)}
          testId="kpi-revenue-month"
        />
        <KpiTile
          label={t('admin.dashboard.fin.revenueYear')}
          value={tnd(financial.revenueYear)}
          testId="kpi-revenue-year"
        />
        <KpiTile
          label={t('admin.dashboard.fin.averageBasket')}
          value={tnd(financial.averageBasket)}
          testId="kpi-average-basket"
        />
        <KpiTile
          label={t('admin.dashboard.fin.ordersTotal')}
          value={count(financial.ordersTotal)}
          {...(financial.ordersCancelled > 0
            ? { hint: t('admin.dashboard.fin.cancelled', { count: financial.ordersCancelled }) }
            : {})}
          testId="kpi-orders-total"
        />
        <KpiTile
          label={t('admin.dashboard.fin.ordersToday')}
          value={count(financial.ordersToday)}
          testId="kpi-orders-today"
        />
      </DashboardSection>

      <DashboardSection
        title={t('admin.dashboard.section.merchants')}
        tiles
        testId="block-merchants"
      >
        <KpiTile
          label={t('admin.dashboard.mer.total')}
          value={count(merchants.total)}
          hint={t('admin.dashboard.mer.withStore', { count: merchants.withStore })}
          to="/merchants"
          testId="kpi-merchants-total"
        />
        <KpiTile
          label={t('admin.dashboard.mer.active')}
          value={count(merchants.active)}
          tone="good"
          to="/merchants?status=active"
          testId="kpi-merchants-active"
        />
        <KpiTile
          label={t('admin.dashboard.mer.pending')}
          value={count(merchants.pending)}
          tone={warnWhenPositive(merchants.pending)}
          to="/merchants?status=pending"
          testId="kpi-merchants-pending"
        />
        <KpiTile
          label={t('admin.dashboard.mer.suspended')}
          value={count(merchants.suspended)}
          to="/merchants?status=suspended"
          testId="kpi-merchants-suspended"
        />
        <KpiTile
          label={t('admin.dashboard.mer.unassigned')}
          value={count(merchants.unassigned)}
          tone={warnWhenPositive(merchants.unassigned)}
          to="/merchants?scope=unassigned"
          testId="kpi-merchants-unassigned"
        />
      </DashboardSection>

      <DashboardSection title={t('admin.dashboard.section.actions')} testId="block-actions">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          <KpiTile
            label={t('admin.dashboard.act.unassigned')}
            value={count(merchants.unassigned)}
            hint={t('admin.dashboard.act.unassignedHint')}
            tone={warnWhenPositive(merchants.unassigned)}
            to="/merchants?scope=unassigned"
            testId="kpi-act-unassigned"
          />
          <KpiTile
            label={t('admin.dashboard.act.pendingOver7d')}
            value={count(merchants.pendingOver7d)}
            hint={t('admin.dashboard.act.pendingOver7dHint')}
            tone={warnWhenPositive(merchants.pendingOver7d)}
            to="/merchants?status=pending"
            testId="kpi-act-pending-over-7d"
          />
          <KpiTile
            label={t('admin.dashboard.act.reassignments')}
            value={count(security.recentReassignments.last7d)}
            hint={t('admin.dashboard.act.reassignmentsHint', {
              last7d: security.recentReassignments.last7d,
              last30d: security.recentReassignments.last30d,
            })}
            to="/portfolios"
            testId="kpi-act-reassignments"
          />
        </div>
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
          <div className="min-w-0 xl:col-span-2">
            <ToHandleTable toHandle={data.toHandle} />
          </div>
          <RecentReassignments latest={security.recentReassignments.latest} />
        </div>
      </DashboardSection>

      <DashboardSection title={t('admin.dashboard.section.trends')} testId="block-trends">
        <div
          role="group"
          aria-label={t('admin.dashboard.trend.range')}
          className="flex flex-wrap gap-2"
        >
          {(Object.keys(TREND_RANGES) as TrendRangeId[]).map((id) => (
            <Button
              key={id}
              size="sm"
              variant={id === range ? 'primary' : 'secondary'}
              aria-pressed={id === range}
              onClick={() => {
                onRange(id);
              }}
            >
              {t(RANGE_LABEL[id])}
            </Button>
          ))}
        </div>
        <TrendsRow trends={trends} />
      </DashboardSection>

      <DashboardSection title={t('admin.dashboard.section.admins')} testId="block-admins">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          <KpiTile
            label={t('admin.dashboard.adm.total')}
            value={count(admins.total)}
            to="/administrators"
            testId="kpi-admins-total"
          />
          <KpiTile
            label={t('admin.dashboard.adm.active')}
            value={count(admins.active)}
            tone="good"
            testId="kpi-admins-active"
          />
          <KpiTile
            label={t('admin.dashboard.adm.disabled')}
            value={count(admins.disabled)}
            tone={warnWhenPositive(admins.disabled)}
            testId="kpi-admins-disabled"
          />
        </div>
        <AdminsTable admins={admins.perAdmin} total={admins.total} />
      </DashboardSection>

      <DashboardSection title={t('admin.dashboard.section.quality')} tiles testId="block-quality">
        <KpiTile
          label={t('admin.dashboard.qua.neverSignedIn')}
          value={count(merchants.neverSignedIn)}
          hint={t('admin.dashboard.qua.neverSignedInHint')}
          tone={warnWhenPositive(merchants.neverSignedIn)}
          testId="kpi-never-signed-in"
        />
        <KpiTile
          label={t('admin.dashboard.qua.provisionalExpired')}
          value={count(merchants.provisionalExpired)}
          hint={t('admin.dashboard.qua.provisionalExpiredHint')}
          tone={warnWhenPositive(merchants.provisionalExpired)}
          testId="kpi-provisional-expired"
        />
        <KpiTile
          label={t('admin.dashboard.qua.withoutOwner')}
          value={count(merchants.withoutOwner)}
          hint={t('admin.dashboard.qua.withoutOwnerHint')}
          tone={warnWhenPositive(merchants.withoutOwner)}
          testId="kpi-without-owner"
        />
        <KpiTile
          label={t('admin.dashboard.qua.withoutProduct')}
          value={count(merchants.withoutProduct)}
          hint={t('admin.dashboard.qua.withoutProductHint')}
          tone={warnWhenPositive(merchants.withoutProduct)}
          testId="kpi-without-product"
        />
      </DashboardSection>

      <DashboardSection title={t('admin.dashboard.section.security')} tiles testId="block-security">
        <KpiTile
          label={t('admin.dashboard.sec.superadmins')}
          value={count(security.superadmins)}
          to="/administrators"
          testId="kpi-superadmins"
        />
        <KpiTile
          label={t('admin.dashboard.sec.platformAdmins')}
          value={count(security.platformAdmins)}
          to="/administrators"
          testId="kpi-platform-admins"
        />
        <KpiTile
          label={t('admin.dashboard.sec.logins')}
          value={count(security.loginsToday)}
          hint={t('admin.dashboard.sec.loginsHint', { count: security.loginsTodayPlatform })}
          testId="kpi-logins"
        />
        <KpiTile
          label={t('admin.dashboard.sec.failed')}
          value={count(security.failedToday)}
          hint={t('admin.dashboard.sec.failedHint', {
            count: security.failedTodayPlatform,
            throttled: security.throttledToday,
          })}
          tone={warnWhenPositive(security.failedTodayPlatform)}
          testId="kpi-failed"
        />
        <KpiTile
          label={t('admin.dashboard.sec.locked')}
          value={count(security.lockedAccounts)}
          tone={warnWhenPositive(security.lockedAccounts)}
          testId="kpi-locked"
        />
        <KpiTile
          label={t('admin.dashboard.sec.promotions')}
          value={count(security.pendingPromotions)}
          tone={security.pendingPromotions > 0 ? 'info' : 'neutral'}
          to="/administrators"
          testId="kpi-promotions"
        />
      </DashboardSection>

      <DashboardSection title={t('admin.dashboard.section.products')} testId="block-products">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          <KpiTile
            label={t('admin.dashboard.pro.total')}
            value={count(products.total)}
            testId="kpi-products-total"
          />
          <KpiTile
            label={t('admin.dashboard.pro.active')}
            value={count(products.active)}
            tone="good"
            testId="kpi-products-active"
          />
          <KpiTile
            label={t('admin.dashboard.pro.outOfStock')}
            value={count(products.outOfStock)}
            tone={warnWhenPositive(products.outOfStock)}
            testId="kpi-products-out"
          />
        </div>
        <TopProductsTable products={products.topSold} />
      </DashboardSection>

      <DashboardSection
        title={t('admin.dashboard.section.topMerchants')}
        testId="block-top-merchants"
      >
        <TopMerchantsTable merchants={data.topMerchants} />
      </DashboardSection>
    </>
  );
}

/** The three curves. They load apart from the figures: a slow or failed curve never hides the cards. */
function TrendsRow({
  trends,
}: {
  readonly trends: {
    readonly data: DashboardTrends | undefined;
    readonly isPending: boolean;
    readonly isError: boolean;
  };
}): ReactElement {
  const { t, locale } = useI18n();

  if (trends.isError) {
    return (
      <Card className="p-4">
        <p role="status" className="text-sm text-inkSoft">
          {t('admin.dashboard.chartLoadError')}
        </p>
      </Card>
    );
  }
  if (trends.isPending || trends.data === undefined) {
    return (
      <div
        className="grid gap-4 md:grid-cols-2 xl:grid-cols-3"
        data-testid="dashboard-trends-loading"
      >
        {[0, 1, 2].map((index) => (
          <Card key={index} className="p-4">
            <Skeleton shape="block" height="12rem" />
          </Card>
        ))}
      </div>
    );
  }

  const { bucket, series } = trends.data;
  const compact = new Intl.NumberFormat(
    locale === 'ar' ? 'ar-TN-u-nu-latn' : locale === 'fr' ? 'fr-TN' : 'en-TN',
    { notation: 'compact', maximumFractionDigits: 1 },
  );
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="dashboard-trends">
      <TrendChart
        title={`${t('admin.dashboard.trend.sales')} (${t('admin.dashboard.trend.currency')})`}
        points={series.map((point) => ({ period: point.period, value: point.revenue }))}
        bucket={bucket}
        kind="line"
        formatValue={(value) => formatTnd(value, locale)}
        formatAxis={(value) => compact.format(value)}
        color="var(--ecom-accent)"
        testId="chart-sales"
      />
      <TrendChart
        title={t('admin.dashboard.trend.orders')}
        points={series.map((point) => ({ period: point.period, value: point.orders }))}
        bucket={bucket}
        kind="bar"
        formatValue={(value) => formatCount(value, locale)}
        formatAxis={(value) => compact.format(value)}
        color="var(--ecom-info)"
        testId="chart-orders"
      />
      <TrendChart
        title={t('admin.dashboard.trend.registrations')}
        points={series.map((point) => ({ period: point.period, value: point.registrations }))}
        bucket={bucket}
        kind="bar"
        formatValue={(value) => formatCount(value, locale)}
        formatAxis={(value) => compact.format(value)}
        color="var(--ecom-good)"
        testId="chart-registrations"
      />
    </div>
  );
}
